import { debugManager, rejectAfter } from '@tunein/web-utils';
import { isBoolean, isNil, omitBy, pickBy } from 'es-toolkit';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import { BidderInitTimeoutError, INIT_MAX_ATTEMPTS, INIT_RETRY_COOLDOWN_MS, INIT_TIMEOUT_MS, } from '../headerBidder/initRetry';
import { renameMultiBidKeys } from '../headerBidder/renameMultiBidKeys';
import { loadMagnite } from './loadMagnite';
const ONE_MINUTE_IN_MILLISECONDS = 60_000;
const BIDS_FETCH_FAILSAFE_PADDING_MS = 1_000;
const debug = debugManager.createDebugger('MAG', '[Web Common] Magnite Bidder');
const debugPrebid = debugManager.createDebugger('PB', '[Web Common] Prebid.js');
export class MagniteBidder extends EventEmitter {
    static #initPromise;
    static #initAttempts = 0;
    static #lastInitAttemptAt;
    static #didInitSucceed = false;
    static #hasLoggedInitFailure = false;
    #options;
    #ready;
    #logError;
    #consentData = {};
    #isFetchingVideoBids = false;
    #isVideoBidsPollingActive = false;
    #nextVideoBids;
    #readyVideoBids;
    #videoBidsPollIntervalId;
    #videoBidsReady;
    constructor(options) {
        super();
        this.#options = {
            bidsFetchTimeoutMs: 5000,
            videoBidsFetchIntervalMinutes: 60,
            ...(options && omitBy(options, isNil)),
        };
        this.#logError = this.#options.logError;
        this.#ready = Promise.withResolvers();
    }
    get hasVideoBids() {
        const { adParams = {} } = this.#readyVideoBids || {};
        return Boolean(Object.keys(adParams).length);
    }
    get ready() {
        return this.#ready.promise;
    }
    async init({ consentData, scriptUrlOverride }) {
        this.#consentData = consentData || {};
        await this.#ensureReady(scriptUrlOverride);
        this.#ready.resolve();
    }
    async #ensureReady(scriptUrlOverride) {
        if (MagniteBidder.#didInitSucceed) {
            return true;
        }
        if (MagniteBidder.#initPromise) {
            try {
                await MagniteBidder.#initPromise;
            }
            catch {
            }
            return MagniteBidder.#didInitSucceed;
        }
        if (MagniteBidder.#initAttempts >= INIT_MAX_ATTEMPTS) {
            return false;
        }
        const now = Date.now();
        const lastAttemptAt = MagniteBidder.#lastInitAttemptAt ?? 0;
        if (MagniteBidder.#initAttempts > 0 && now - lastAttemptAt < INIT_RETRY_COOLDOWN_MS) {
            return false;
        }
        MagniteBidder.#initAttempts += 1;
        MagniteBidder.#lastInitAttemptAt = now;
        const attempt = MagniteBidder.#initAttempts;
        const inFlight = MagniteBidder.#initLibrary(scriptUrlOverride, this.#options.bidsFetchTimeoutMs);
        MagniteBidder.#initPromise = inFlight;
        try {
            await inFlight;
            MagniteBidder.#didInitSucceed = true;
        }
        catch (error) {
            this.#maybeLogInitFailure(attempt, error);
        }
        finally {
            if (MagniteBidder.#initPromise === inFlight) {
                MagniteBidder.#initPromise = undefined;
            }
        }
        return MagniteBidder.#didInitSucceed;
    }
    #maybeLogInitFailure(attempt, error) {
        if (MagniteBidder.#hasLoggedInitFailure) {
            return;
        }
        MagniteBidder.#hasLoggedInitFailure = true;
        const errorMessage = error instanceof Error ? error.message : String(error);
        const reason = error instanceof BidderInitTimeoutError ? 'timeout' : 'error';
        debug.verbose('Magnite bidder init failed.', { attempt, reason, errorMessage });
        this.#logError?.('Magnite bidder init failed', {
            attempt,
            maxAttempts: INIT_MAX_ATTEMPTS,
            reason,
            errorMessage,
        });
    }
    updateConsent(consentData) {
        this.#consentData = consentData || {};
    }
    consumeVideoBidsAdParams() {
        const bids = this.#readyVideoBids;
        this.#readyVideoBids = undefined;
        return bids;
    }
    async fetchBids(slots) {
        const { bidsMap } = await this.#fetchBidsWithRequests(slots);
        return bidsMap ? this.#flattenBids(bidsMap) : undefined;
    }
    async pollAndCacheVideoBids() {
        if (!this.#hasVideoUnitIds) {
            debug.verbose('Skipping Magnite video bids polling because no video unit ids are configured.');
            return;
        }
        const wasPollingActive = this.#isVideoBidsPollingActive;
        if (!wasPollingActive) {
            this.#isVideoBidsPollingActive = true;
            this.#fetchAndStoreVideoBids();
            this.#startVideoBidsPollInterval();
        }
        if (!this.#hasReadyVideoBidsSnapshot && this.#isFetchingVideoBids) {
            await this.#videoBidsReady?.promise;
        }
        if (!this.#isVideoBidsPollingActive) {
            return;
        }
        this.#promoteNextVideoBids();
        if (!this.#hasReadyVideoBidsSnapshot && !this.#isFetchingVideoBids) {
            if (wasPollingActive) {
                this.#restartVideoBidsPollInterval();
            }
            this.#fetchAndStoreVideoBids();
            await this.#videoBidsReady?.promise;
            if (!this.#isVideoBidsPollingActive) {
                return;
            }
            this.#promoteNextVideoBids();
        }
        if (wasPollingActive && !this.#nextVideoBids && !this.#isFetchingVideoBids) {
            this.#fetchAndStoreVideoBids();
        }
    }
    stopVideoBidsPolling() {
        this.#isVideoBidsPollingActive = false;
        this.#isFetchingVideoBids = false;
        this.#nextVideoBids = undefined;
        this.#readyVideoBids = undefined;
        this.#videoBidsReady?.resolve();
        this.#videoBidsReady = undefined;
        if (this.#videoBidsPollIntervalId) {
            clearInterval(this.#videoBidsPollIntervalId);
            this.#videoBidsPollIntervalId = undefined;
        }
    }
    get #hasVideoUnitIds() {
        return this.#options.videoUnitIds?.length;
    }
    get #hasReadyVideoBidsSnapshot() {
        return Boolean(this.#readyVideoBids);
    }
    #startVideoBidsPollInterval() {
        if (this.#videoBidsPollIntervalId) {
            clearInterval(this.#videoBidsPollIntervalId);
        }
        this.#videoBidsPollIntervalId = setInterval(() => {
            this.#fetchAndStoreVideoBids();
        }, this.#options.videoBidsFetchIntervalMinutes * ONE_MINUTE_IN_MILLISECONDS);
    }
    #restartVideoBidsPollInterval() {
        if (!this.#isVideoBidsPollingActive) {
            return;
        }
        this.#startVideoBidsPollInterval();
    }
    #promoteNextVideoBids() {
        if (!this.#readyVideoBids && this.#nextVideoBids) {
            this.#readyVideoBids = this.#nextVideoBids;
            this.#nextVideoBids = undefined;
        }
    }
    static async #initLibrary(scriptUrlOverride, bidderTimeout) {
        const { promise, resolve, reject } = Promise.withResolvers();
        let settled = false;
        const timeoutId = setTimeout(() => {
            if (settled)
                return;
            settled = true;
            reject(new BidderInitTimeoutError('Magnite'));
        }, INIT_TIMEOUT_MS);
        loadMagnite(scriptUrlOverride);
        if (!window.pbjs) {
            window.pbjs = { que: [] };
        }
        const pbjs = window.pbjs;
        pbjs.que.push(() => {
            if (settled)
                return;
            settled = true;
            clearTimeout(timeoutId);
            try {
                const rubiconConfig = pbjs.getConfig('rubicon');
                rubiconConfig.waitForGamSlots = false;
                pbjs.setConfig({
                    rubicon: rubiconConfig,
                    bidderTimeout: bidderTimeout ?? 5000,
                    debugging: {
                        enabled: debugPrebid.isVerboseActive(),
                    },
                });
                resolve();
            }
            catch (error) {
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        });
        await promise;
    }
    #getOrtb2Regs() {
        const { gdprApplies, gdprTcString, usPrivacyString } = this.#consentData;
        const regs = {};
        const ext = {};
        if (isBoolean(gdprApplies)) {
            regs.gdpr = gdprApplies ? 1 : 0;
            ext.gdpr = gdprApplies ? 1 : 0;
        }
        if (gdprTcString) {
            ext.consent = gdprTcString;
        }
        if (usPrivacyString) {
            regs.us_privacy = usPrivacyString;
            ext.us_privacy = usPrivacyString;
        }
        if (Object.keys(ext).length) {
            regs.ext = ext;
        }
        return Object.keys(regs).length ? regs : undefined;
    }
    async #fetchBidsWithRequests(slots) {
        const slotRequests = slots.map((slot) => ({ slot, requestId: uuidv4() }));
        slotRequests.forEach(this.#emitBidEvent('bidRequested'));
        const timeout = this.#options.bidsFetchTimeoutMs;
        const failsafeTimeout = timeout + BIDS_FETCH_FAILSAFE_PADDING_MS;
        const isReady = await this.#ensureReady();
        if (!isReady) {
            debug.verbose('Magnite fetchBids short-circuited: bidder not ready.');
            slotRequests.forEach(this.#emitBidEvent('bidRequestFailed'));
            return { slotRequests };
        }
        try {
            const bidsMap = await rejectAfter(this.#requestBids(slots), failsafeTimeout, new Error('Bids fetch timeout'));
            debug.verbose('Magnite bids response received.', bidsMap);
            slotRequests.forEach(this.#emitBidEvent('bidResponseReceived', bidsMap));
            return { bidsMap, slotRequests };
        }
        catch (error) {
            debug.verbose('Magnite bids request failed.', error);
            slotRequests.forEach(this.#emitBidEvent('bidRequestFailed'));
            return { slotRequests };
        }
    }
    #emitBidEvent = (eventName, bidsMap) => (requestedSlot) => {
        const { slot, requestId } = requestedSlot;
        const event = {
            network: 'magnite',
            unitId: slot.name,
            requestId,
        };
        if (bidsMap) {
            event.hasBid = this.#slotHasBid(slot, bidsMap);
        }
        this.emit(eventName, event);
    };
    #slotHasBid(slot, bidsMap) {
        if (!bidsMap) {
            return false;
        }
        const slotCode = slot.divId || slot.name;
        const entry = bidsMap[slotCode];
        const bids = entry?.bids;
        return bids?.some((bid) => Boolean(this.#getBidTargeting(bid))) ?? false;
    }
    #getBidTargeting(bid) {
        const targeting = bid?.adserverTargeting;
        if (!targeting) {
            return undefined;
        }
        const params = pickBy(targeting, Boolean);
        return Object.keys(params).length ? params : undefined;
    }
    #buildVideoMagniteBids = (bidsMap, slotRequests) => {
        const bids = this.#flattenBids(bidsMap);
        const adParams = bids.reduce((params, bid, index) => {
            const bidParams = this.#getBidTargeting(bid);
            if (!bidParams) {
                return params;
            }
            return {
                ...params,
                ...renameMultiBidKeys(bidParams, index),
            };
        }, {});
        return {
            adParams: Object.keys(adParams).length ? adParams : undefined,
            bidRequests: slotRequests.map(({ requestId, slot }) => ({
                requestId,
                network: 'magnite',
                hasBid: this.#slotHasBid(slot, bidsMap),
            })),
        };
    };
    #flattenBids(bidsMap) {
        if (!bidsMap) {
            return [];
        }
        const flattened = [];
        for (const [adUnitCode, entry] of Object.entries(bidsMap)) {
            const entryBids = entry?.bids;
            if (!entryBids?.length) {
                continue;
            }
            for (const bid of entryBids) {
                flattened.push({ ...bid, adUnitCode: bid.adUnitCode || adUnitCode });
            }
        }
        return flattened;
    }
    async #fetchAndStoreVideoBids() {
        const { videoUnitIds } = this.#options;
        if (!videoUnitIds?.length || !this.#isVideoBidsPollingActive || this.#isFetchingVideoBids) {
            return;
        }
        this.#isFetchingVideoBids = true;
        const videoBidsReady = Promise.withResolvers();
        this.#videoBidsReady = videoBidsReady;
        try {
            const slots = videoUnitIds.map((unitId) => ({ name: unitId }));
            const { bidsMap, slotRequests } = await this.#fetchBidsWithRequests(slots);
            const nextVideoBids = this.#buildVideoMagniteBids(bidsMap, slotRequests);
            if (this.#isVideoBidsPollingActive && this.#videoBidsReady === videoBidsReady) {
                this.#nextVideoBids = nextVideoBids;
            }
        }
        finally {
            if (this.#videoBidsReady === videoBidsReady) {
                this.#isFetchingVideoBids = false;
                this.#videoBidsReady = undefined;
            }
            videoBidsReady.resolve();
        }
    }
    async #requestBids(slots) {
        const { promise, resolve } = Promise.withResolvers();
        const regs = this.#getOrtb2Regs();
        window.pbjs?.que.push(() => {
            if (!window.pbjs?.rp) {
                resolve(undefined);
                return;
            }
            if (regs) {
                window.pbjs.setConfig({ ortb2: { regs } });
            }
            window.pbjs.rp.requestBids({
                callback: (bids) => resolve(bids),
                slotMap: slots,
            });
        });
        return promise;
    }
}
